import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import axiosInstance from "../api/axiosInstance";

const AddEditEmployeeMaster = ({ editingData, onClose, onSuccess }) => {
  let storedUser = {};
  try {
    storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    storedUser = {};
  }

  const restId = storedUser?.restaurantId || "";
  const groupId = storedUser?.groupId || 1;
  const userId = storedUser?.userId || 0;

  /* ================= STATE ================= */
  const [empName, setEmpName] = useState("");
  const [empMobileNo, setEmpMobileNo] = useState("");
  const [empType, setEmpType] = useState("Staff");
  const [empStatus, setEmpStatus] = useState("Working");
  const [joiningDate, setJoiningDate] = useState("");

  const [deptOptions, setDeptOptions] = useState([]);
  const [desigOptions, setDesigOptions] = useState([]);
  const [selectedDept, setSelectedDept] = useState(null);
  const [selectedDesig, setSelectedDesig] = useState(null);

  const [restaurants, setRestaurants] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [loadingRest, setLoadingRest] = useState(false);

  const [menuDeptOpen, setMenuDeptOpen] = useState(false);
  const [menuDesigOpen, setMenuDesigOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  /* ================= REFS ================= */
  const restaurantRef = useRef(null);
  const nameRef = useRef(null);
  const mobileRef = useRef(null);
  const deptRef = useRef(null);
  const desigRef = useRef(null);
  const typeRef = useRef(null);
  const dateRef = useRef(null);
  const statusRef = useRef(null);
  const saveRef = useRef(null);

  /* ================= HELPERS ================= */
  const toTitleCase = (text) =>
    text
      ? text
          .toLowerCase()
          .split(" ")
          .filter(Boolean)
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ")
      : "";

  const handleFocusSelect = (ref) => setTimeout(() => ref?.current?.select?.(), 0);

  /* ================= FETCH ================= */
  const fetchDepartments = async () => {
    try {
      const res = await axiosInstance.get(`/DepartmentMaster/GetByGroup?groupId=${groupId}`);
      setDeptOptions((res.data || []).map((d) => ({ value: d.departmentId, label: d.departmentName })));
    } catch (err) {
      console.error("Department fetch error:", err);
    }
  };

  const fetchDesignations = async () => {
    try {
      const res = await axiosInstance.get(`/DesignationMaster/GetByGroup?groupId=${groupId}`);
      setDesigOptions((res.data || []).map((d) => ({ value: d.designationId, label: d.designationName })));
    } catch (err) {
      console.error("Designation fetch error:", err);
    }
  };

  const fetchRestaurants = async () => {
    try {
      setLoadingRest(true);
      const res = await axiosInstance.get(`/RestaurantMaster/GetByGroup?groupId=${groupId}`);
      const options = (res.data || []).map((r) => ({ value: r.restId, label: r.restaurantName }));
      setRestaurants(options);

      const defaultId = editingData?.restId || restId;
      const found = options.find((x) => Number(x.value) === Number(defaultId));
      if (found) setSelectedRestaurant(found);
      else if (options.length) setSelectedRestaurant(options[0]);
    } catch (err) {
      console.error("Restaurant fetch error:", err);
    } finally {
      setLoadingRest(false);
    }
  };

  useEffect(() => {
    fetchDepartments();
    fetchDesignations();
    fetchRestaurants();
  }, []);

  /* ================= LOAD EDIT ================= */
  useEffect(() => {
    if (!editingData) return;

    setEmpName(editingData.empName || "");
    setEmpMobileNo(editingData.empMobileNo || "");
    setEmpType(editingData.empType || "Staff");
    setEmpStatus(editingData.empStatus || "Working");
    setJoiningDate(
      editingData.joiningDate !== "0001-01-01T00:00:00.000Z"
        ? editingData.joiningDate.substring(0, 10)
        : ""
    );
  }, [editingData]);

  useEffect(() => {
    if (!editingData) return;

    if (deptOptions.length && editingData.departmentId) {
      const dept = deptOptions.find((d) => d.value === editingData.departmentId);
      setSelectedDept(dept || null);
    }

    if (desigOptions.length && editingData.designationId) {
      const desig = desigOptions.find((d) => d.value === editingData.designationId);
      setSelectedDesig(desig || null);
    }
  }, [deptOptions, desigOptions, editingData]);

  /* ================= AUTO FOCUS ================= */
  useEffect(() => {
    setTimeout(() => {
      restaurantRef.current?.focus?.();
    }, 300);
  }, []);

  /* ================= KEYBOARD NAV ================= */
const handleKeyDown = (e, nextRef, prevRef, options = {}) => {
  const { mobileCheck = false, isLast = false } = options;

  // If react-select menu is open, let it handle Enter
  const isReactSelectInput =
    e.target?.id?.includes("react-select") ||
    e.target?.className?.includes("react-select");

  if (isReactSelectInput && e.key === "Enter") {
    return;
  }

  // Mobile validation
  if (mobileCheck && e.key === "Enter" && empMobileNo.length !== 10) {
    e.preventDefault();
    return;
  }

  if (e.key === "Enter") {
    e.preventDefault();

    if (nextRef?.current) {
      nextRef.current.focus();
    } else if (isLast) {
      handleSubmit();
    }
  }

  if (e.key === "Escape") {
    e.preventDefault();

    if (prevRef?.current) {
      prevRef.current.focus();
    } else {
      onClose?.();
    }
  }
};

  /* ================= SUBMIT ================= */
  const handleSubmit = async () => {
    if (loading) return;

    if (!selectedRestaurant) return alert("Select restaurant");
    if (!empName.trim()) return alert("Enter employee name");
    if (!selectedDept) return alert("Select department");
    if (!selectedDesig) return alert("Select designation");

    setLoading(true);
    try {
      await axiosInstance.post("/EmployeeMaster/UpsertEmployee", {
        groupId,
        restId: selectedRestaurant.value,
        activeRestId: selectedRestaurant.value,
        empId: editingData?.empId || 0,
        empName: toTitleCase(empName.trim()),
        empMobileNo,
        empType: toTitleCase(empType.trim()),
        empDepartmentId: selectedDept.value,
        empDesignationId: selectedDesig.value,
        empJoiningDate: joiningDate ? new Date(joiningDate).toISOString() : new Date().toISOString(),
        empStatus: toTitleCase(empStatus.trim()),
        userId,
      });
      onSuccess?.();
      onClose?.();
    } catch (err) {
      console.error("Save error:", err);
      alert("Save failed");
    } finally {
      setLoading(false);
    }
  };

  /* ================= UI ================= */
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[9999] p-3">
      <div className="bg-white w-full max-w-xl rounded-xl shadow-xl p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-bold mb-4">{editingData ? "Edit Employee" : "Add Employee"}</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Restaurant */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium mb-1">Restaurant *</label>
           <Select
  ref={restaurantRef}
  options={restaurants}
  value={selectedRestaurant}
  onChange={(option) => {
    setSelectedRestaurant(option);
    setTimeout(() => nameRef.current?.focus(), 60);
  }}
  onKeyDown={(e) => handleKeyDown(e, nameRef, null)}
  isLoading={loadingRest}
  placeholder="Select restaurant"
  menuPortalTarget={document.body}
  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
  isSearchable
  openMenuOnFocus={true}
  tabSelectsValue={false}
  menuPosition="fixed"
/>
          </div>

          {/* Name */}
          <InputText
            label="Employee Name *"
            value={empName}
            setValue={setEmpName}
            refProp={nameRef}
            nextRef={mobileRef}
            prevRef={restaurantRef}
            handleKeyDown={handleKeyDown}
            handleBlurFormat={() => setEmpName(toTitleCase(empName))}
            handleFocusSelect={handleFocusSelect}
          />

          {/* Mobile */}
          <InputText
            label="Mobile *"
            value={empMobileNo}
            setValue={(val) => setEmpMobileNo(val.replace(/\D/g, "").slice(0, 10))}
            refProp={mobileRef}
            nextRef={deptRef}
            prevRef={nameRef}
            handleKeyDown={(e) => handleKeyDown(e, deptRef, nameRef, { mobileCheck: true })}
            handleBlurFormat={null}
            handleFocusSelect={handleFocusSelect}
          />

          {/* Department */}
          <div>
            <label className="block text-sm font-medium mb-1">Department *</label>
        <Select
  ref={deptRef}
  options={deptOptions}
  value={selectedDept}
  onChange={(val) => {
    setSelectedDept(val);
    setTimeout(() => desigRef.current?.focus(), 80);
  }}
  placeholder="Select department"
  menuPortalTarget={document.body}
  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
  isSearchable
  tabSelectsValue={false}
  openMenuOnFocus={true}
  onKeyDown={(e) => handleKeyDown(e, desigRef, mobileRef)}
/>
          </div>

          {/* Designation */}
          <div>
            <label className="block text-sm font-medium mb-1">Designation *</label>
           <Select
  ref={desigRef}
  options={desigOptions}
  value={selectedDesig}
  onChange={(val) => {
    setSelectedDesig(val);
    setTimeout(() => typeRef.current?.focus(), 80);
  }}
  placeholder="Select designation"
  menuPortalTarget={document.body}
  styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
  isSearchable
  tabSelectsValue={false}
  openMenuOnFocus={true}
  onKeyDown={(e) => handleKeyDown(e, typeRef, deptRef)}
/>
          </div>

          {/* Type */}
          <InputText
            label="Employee Type"
            value={empType}
            setValue={setEmpType}
            refProp={typeRef}
            nextRef={dateRef}
            prevRef={desigRef}
            handleKeyDown={handleKeyDown}
            handleBlurFormat={() => setEmpType(toTitleCase(empType))}
            handleFocusSelect={handleFocusSelect}
          />

          {/* Joining Date */}
          <InputDate
            label="Joining Date"
            value={joiningDate}
            setValue={setJoiningDate}
            refProp={dateRef}
            nextRef={statusRef}
            prevRef={typeRef}
            handleKeyDown={handleKeyDown}
            handleFocusSelect={handleFocusSelect}
          />

          {/* Status */}
          <InputText
            label="Employee Status"
            value={empStatus}
            setValue={setEmpStatus}
            refProp={statusRef}
            nextRef={saveRef}
            prevRef={dateRef}
            handleKeyDown={handleKeyDown}
            handleBlurFormat={() => setEmpStatus(toTitleCase(empStatus))}
            handleFocusSelect={handleFocusSelect}
          />
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-3 mt-5">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-gray-500 text-white hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            ref={saveRef}
            onClick={handleSubmit}
            onKeyDown={(e) => handleKeyDown(e, null, statusRef, { isLast: true })}
            disabled={loading}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ================= SMALL INPUT COMPONENTS ================= */
const InputText = ({ label, value, setValue, refProp, nextRef, prevRef, handleKeyDown, handleBlurFormat, handleFocusSelect }) => (
  <div>
    <label className="block text-sm font-medium mb-1">{label}</label>
    <input
      ref={refProp}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={handleBlurFormat}
      onFocus={() => handleFocusSelect(refProp)}
      onKeyDown={(e) => handleKeyDown(e, nextRef, prevRef)}
      className="w-full border rounded-lg px-3 py-2"
    />
  </div>
);

const InputDate = ({ label, value, setValue, refProp, nextRef, prevRef, handleKeyDown, handleFocusSelect }) => (
  <div>
    <label className="block text-sm font-medium mb-1">{label}</label>
    <input
      type="date"
      ref={refProp}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onFocus={() => handleFocusSelect(refProp)}
      onKeyDown={(e) => handleKeyDown(e, nextRef, prevRef)}
      className="w-full border rounded-lg px-3 py-2"
    />
  </div>
);

export default AddEditEmployeeMaster;